"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";

import Badge2026 from "@/components/sections/2026/ui/Badge2026";
import ResponsiveCopy from "@/components/sections/2026/ui/ResponsiveCopy";

// Section eyebrow above the showcase: reused glass badge → headline → subtitle.
// Each item rises + fades in on view with a small stagger (same pattern as the
// Hero). Sits in normal flow above the pinned/stacked showcase, so it scrolls
// away before the pinning begins.
//
// The hidden state is the same whether or not the visitor asks for reduced
// motion — it is what the server writes into the HTML, and the server cannot
// know that preference, so branching it is a hydration mismatch. Reduced motion
// lives in the transition: the travel gets zero duration, the fade still plays.
const HIDDEN = { opacity: 0, y: 24 };

const REDUCED = { opacity: { duration: 0.3 }, y: { duration: 0 } };

export default function PortfolioHeader() {
  const t = useTranslations("home.portfolio");
  const reduce = useReducedMotion();
  const variants = {
    hidden: HIDDEN,
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: reduce
        ? REDUCED
        : { duration: 0.5, delay: i * 0.12, ease: "easeOut" },
    }),
  };
  const reveal = {
    initial: "hidden",
    whileInView: "show",
    viewport: { once: true, amount: 0.4 },
    variants,
  };

  return (
    <div className="mx-auto max-w-[1440px] px-6 sm:px-8 md:px-12 lg:px-16">
      <div className="flex max-w-[938px] flex-col items-start gap-[18px]">
        <motion.div {...reveal} custom={0}>
          <Badge2026>{t("badge")}</Badge2026>
        </motion.div>

        <motion.h2
          {...reveal}
          custom={1}
          className="text-3xl font-bold text-white th:leading-[1.35] lg:text-[36px]"
        >
          <ResponsiveCopy
            desktop={t("headline")}
            mobile={t("headlineMobile")}
          />
        </motion.h2>

        <motion.p
          {...reveal}
          custom={2}
          className="text-lg font-normal text-white lg:text-xl"
        >
          <ResponsiveCopy
            desktop={t("subtitle")}
            mobile={t("subtitleMobile")}
          />
        </motion.p>
      </div>
    </div>
  );
}
